import Button from "@/components/ui/Button";
import TextField from "@/components/ui/Textfield";
import useAuthStore from "@/store/authStore";
import useGlobalStore from "@/store/globalStore";
import { useState, FormEvent, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import { Eye, EyeOff } from "lucide-react";
import AgencySelection from "./components/agency-selection";

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const navigate = useNavigate();

  const {
    login,
    user,
    requiresAgencySelection,
    agencies,
    activeAgencyId,
    selectAgency,
  } = useAuthStore(
    useShallow((state) => ({
      user: state.user,
      login: state.login,
      requiresAgencySelection: state.requiresAgencySelection,
      agencies: state.agencies,
      activeAgencyId: state.activeAgencyId,
      selectAgency: state.selectAgency,
    })),
  );

  const setError = useGlobalStore((state) => state.setError);

  /**
   * Where to land after a successful sign-in.
   *
   * `PrivateRoute` sets `?redirect=` when it bounces an unauthenticated visitor away from
   * a protected page, so a deep link from an email survives the round trip. Only
   * same-origin paths are accepted — a value starting with `//` or a scheme would be an
   * open redirect, so anything but a single leading slash falls back to the dashboard.
   */
  const destination = (() => {
    const requested = searchParams.get("redirect");

    if (
      !requested ||
      !requested.startsWith("/") ||
      requested.startsWith("//")
    ) {
      return "/dashboard";
    }

    return requested;
  })();

  // Pre-fill email from query params if coming from invite
  useEffect(() => {
    const emailParam = searchParams.get("email");
    if (emailParam) {
      setEmail(decodeURIComponent(emailParam));
    }
  }, [searchParams]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Simple validation example
    if (!email) {
      setErrorMessage("Email is required.");
      return;
    }
    if (!password) {
      setErrorMessage("Password is required.");
      return;
    }
    setIsLoading(true);

    try {
      const response = await login({ email, password });

      // If coming from invite with agency_id, auto-select that agency
      const fromInvite = searchParams.get("from_invite") === "true";
      const inviteAgencyId = searchParams.get("agency_id");

      if (
        fromInvite &&
        inviteAgencyId &&
        response.data?.requires_agency_selection
      ) {
        try {
          // Auto-select the agency from the invite
          await selectAgency(inviteAgencyId);
          navigate(destination);
          return;
        } catch (err) {
          // If auto-select fails, log error but continue to manual selection
          console.error("Failed to auto-select agency:", err);
          // Fall through to show agency selection screen
        }
      }

      // If the backend asked us to pick an agency, stay on this screen and let
      // the agency-selection view (rendered below) handle the next step.
      if (response.data?.requires_agency_selection) {
        return;
      }
      navigate(destination);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    // Auto-redirect only when we have a usable session (token + active agency
    // for normal users, or a fully-loaded user). When the backend asked for
    // agency selection, stay on this screen so the selector is rendered.
    if (requiresAgencySelection) return;
    if ((token && activeAgencyId) || user) {
      navigate(destination);
    }
  }, [navigate, destination, user, requiresAgencySelection, activeAgencyId]);

  if (requiresAgencySelection) {
    const inviteAgencyId = searchParams.get("agency_id");
    return (
      <AgencySelection
        agencies={agencies}
        preselectedAgencyId={inviteAgencyId || undefined}
      />
    );
  }

  const fromInvite = searchParams.get("from_invite") === "true";

  return (
    <div className="min-h-screen flex flex-col">
      {/* Top Banner */}
      <div className="w-full bg-[#ea33c0] py-20 text-center">
        <h1 className="text-white text-3xl md:text-5xl font-bold">
          MediaBridge
        </h1>
      </div>

      {/* Login Section */}
      <div className="flex flex-col items-center justify-center flex-grow">
        <div className="max-w-md w-full">
          <h2 className="text-2xl font-bold text-center mb-6">User Login</h2>

          {/* Invite success message */}
          {fromInvite && (
            <div className="mb-4 w-2/3 mx-auto text-green-700 bg-green-100 border border-green-400 text-sm px-4 py-2 rounded text-center">
              Invite accepted! Please sign in to continue.
            </div>
          )}

          {/* Error Messages */}
          {errorMessage && (
            <div className="mb-4 w-2/3 mx-auto text-red-700 bg-red-100 border border-red-400 text-sm px-4 py-2 rounded">
              {errorMessage}
            </div>
          )}

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="px-5 text-center">
              <TextField
                type="email"
                name="login"
                placeholder="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full md:w-3/4"
              />
            </div>

            <div className="px-5 text-center">
              <div className="inline-block w-full md:w-3/4">
                <TextField
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full"
                  trailingIcon={
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="pointer-events-auto cursor-pointer text-gray-500 hover:text-gray-700 mt-2"
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  }
                />
              </div>
            </div>

            <Button
              type="primary"
              className="mx-auto text-center px-10 text-white"
              isLoading={isLoading}
            >
              Sign in
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
